/**
 * ViralMarketingTab - Admin analytics dashboard for Viral Marketing System
 * Shows shares, referrals, coupons, QR scans, and provides QR code generator
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Camera, Users, Ticket, QrCode, TrendingUp, Download,
  Facebook, Instagram, Star, MapPin, MessageCircle,
  RefreshCw, Calendar, ChevronDown, Copy, ExternalLink,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  Share2, Gift, Award, Loader2, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { api } from '@/utils/apiClient';

// Platform icons
const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  google: Star,
  tripadvisor: MapPin,
  whatsapp: MessageCircle
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, change, changeType, color = 'primary', loading }) => (
  <div className="bg-card border border-border rounded-xl p-4 md:p-5">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-${color}/10 dark:bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 md:w-6 md:h-6 text-${color}`} />
      </div>
      {change !== undefined && (
        <div className={`flex items-center text-xs md:text-sm ${changeType === 'up' ? 'text-green-600 dark:text-green-400' : changeType === 'down' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
          {changeType === 'up' ? <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4" /> : changeType === 'down' ? <ArrowDownRight className="w-3 h-3 md:w-4 md:h-4" /> : null}
          {change}%
        </div>
      )}
    </div>
    <div className="mt-3 md:mt-4">
      {loading ? (
        <div className="h-8 w-20 bg-muted animate-pulse rounded" />
      ) : (
        <p className="text-2xl md:text-3xl font-bold text-foreground">{value?.toLocaleString() || 0}</p>
      )}
      <p className="text-xs md:text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  </div>
);

// Platform breakdown bar
const PlatformBar = ({ platform, count, total, color }) => {
  const Icon = PLATFORM_ICONS[platform] || Share2;
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  
  return (
    <div className="flex items-center gap-3 py-2">
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium capitalize">{platform}</span>
          <span className="text-sm text-muted-foreground">{count} ({percentage}%)</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full ${color} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Top Referrer Row
const ReferrerRow = ({ rank, name, referrals, isAmbassador }) => (
  <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors">
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${rank <= 3 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-muted text-muted-foreground'}`}>
      {rank}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{name}</p>
      {isAmbassador && (
        <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Award className="w-3 h-3" /> Ambassador
        </span>
      )}
    </div>
    <span className="text-sm font-bold text-primary">{referrals}</span>
  </div>
);

export default function ViralMarketingTab() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30');
  
  // Analytics data
  const [overview, setOverview] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [couponStats, setCouponStats] = useState(null);
  
  // QR Code
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrAnalytics, setQrAnalytics] = useState(null);
  
  // Load analytics data
  const loadData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [overviewRes, sharesRes, referralsRes, couponsRes, qrRes] = await Promise.all([
        api.get(`/viral/admin/analytics/overview?days=${period}`),
        api.get(`/viral/admin/analytics/shares?days=${period}`),
        api.get(`/viral/admin/analytics/referrals?days=${period}`),
        api.get(`/viral/admin/analytics/coupons?days=${period}`),
        api.get('/viral/admin/qr/share')
      ]);
      
      setOverview(overviewRes.data);
      setShareStats(sharesRes.data);
      setReferralStats(referralsRes.data);
      setCouponStats(couponsRes.data);
      
      setQrAnalytics(qrRes.data.analytics);
      // Generate QR code
      const dataUrl = await QRCode.toDataURL(qrRes.data.url, {
        width: 400,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('Failed to load viral analytics:', err);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
    setRefreshing(false);
  }, [period]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Download QR code
  const downloadQR = async (format = 'png') => {
    try {
      const shareUrl = 'https://bulaccinocafe.com/share';
      
      if (format === 'svg') {
        const svgString = await QRCode.toString(shareUrl, { type: 'svg', width: 400, margin: 2 });
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulaccino-share-qr.svg';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const dataUrl = await QRCode.toDataURL(shareUrl, { width: 800, margin: 2 });
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'bulaccino-share-qr.png';
        a.click();
      }
      
      toast.success(`QR code downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to download QR code');
    }
  };
  
  // Copy share URL
  const copyShareUrl = () => {
    navigator.clipboard?.writeText('https://bulaccinocafe.com/share');
    toast.success('Share URL copied!');
  };
  
  const metrics = overview?.metrics || {};
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-heading text-foreground">Viral Marketing Analytics</h2>
          <p className="text-sm text-muted-foreground">Track shares, referrals, and campaign performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadData} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          icon={QrCode}
          label="QR Scans"
          value={metrics.qr_scans?.value}
          change={metrics.qr_scans?.change}
          changeType={metrics.qr_scans?.change > 0 ? 'up' : metrics.qr_scans?.change < 0 ? 'down' : null}
          color="blue-500"
          loading={loading}
        />
        <StatCard
          icon={Camera}
          label="Total Shares"
          value={metrics.total_shares?.value}
          change={metrics.total_shares?.change}
          changeType={metrics.total_shares?.change > 0 ? 'up' : metrics.total_shares?.change < 0 ? 'down' : null}
          color="purple-500"
          loading={loading}
        />
        <StatCard
          icon={Users}
          label="Referrals"
          value={metrics.successful_referrals?.value}
          change={metrics.successful_referrals?.change}
          changeType={metrics.successful_referrals?.change > 0 ? 'up' : null}
          color="green-500"
          loading={loading}
        />
        <StatCard
          icon={Ticket}
          label="Coupons Used"
          value={metrics.coupons_used?.value}
          change={metrics.coupons_used?.change}
          changeType={metrics.coupons_used?.change > 0 ? 'up' : null}
          color="amber-500"
          loading={loading}
        />
      </div>
      
      {/* Second Row Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <StatCard
          icon={Star}
          label="Reviews"
          value={metrics.reviews?.value}
          color="red-500"
          loading={loading}
        />
        <StatCard
          icon={Users}
          label="New Accounts"
          value={metrics.new_accounts?.value}
          change={metrics.new_accounts?.change}
          changeType={metrics.new_accounts?.change > 0 ? 'up' : null}
          color="indigo-500"
          loading={loading}
        />
        <StatCard
          icon={Gift}
          label="Points Issued"
          value={metrics.points_issued?.value}
          color="pink-500"
          loading={loading}
        />
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Share Platform Breakdown */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4 md:p-6">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Shares by Platform
          </h3>
          
          {loading ? (
            <div className="space-y-4">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-10 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <PlatformBar 
                platform="facebook" 
                count={shareStats?.by_platform?.facebook?.count || 0}
                total={shareStats?.total_shares || 1}
                color="bg-blue-600"
              />
              <PlatformBar 
                platform="instagram" 
                count={shareStats?.by_platform?.instagram?.count || 0}
                total={shareStats?.total_shares || 1}
                color="bg-gradient-to-r from-purple-600 to-pink-500"
              />
              <PlatformBar 
                platform="google" 
                count={shareStats?.by_platform?.google?.count || 0}
                total={shareStats?.total_shares || 1}
                color="bg-red-500"
              />
              <PlatformBar 
                platform="tripadvisor" 
                count={shareStats?.by_platform?.tripadvisor?.count || 0}
                total={shareStats?.total_shares || 1}
                color="bg-green-600"
              />
              <PlatformBar 
                platform="whatsapp" 
                count={shareStats?.by_platform?.whatsapp?.count || 0}
                total={shareStats?.total_shares || 1}
                color="bg-emerald-500"
              />
            </div>
          )}
          
          {shareStats?.by_content_type && Object.keys(shareStats.by_content_type).length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <h4 className="text-sm font-medium mb-3">Content Types</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(shareStats.by_content_type).map(([type, count]) => (
                  <span key={type} className="px-3 py-1 bg-muted rounded-full text-xs capitalize">
                    {type}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* QR Code Generator */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            Share QR Code
          </h3>
          
          <div className="flex flex-col items-center">
            {qrDataUrl ? (
              <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
                <img src={qrDataUrl} alt="Share QR Code" className="w-40 h-40 md:w-48 md:h-48" />
              </div>
            ) : (
              <div className="w-48 h-48 bg-muted animate-pulse rounded-xl mb-4" />
            )}
            
            <p className="text-xs text-muted-foreground text-center mb-4">
              Scan to share photos & earn points
            </p>
            
            <div className="flex flex-wrap gap-2 justify-center w-full">
              <Button variant="outline" size="sm" onClick={() => downloadQR('png')}>
                <Download className="w-4 h-4 mr-1" /> PNG
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadQR('svg')}>
                <Download className="w-4 h-4 mr-1" /> SVG
              </Button>
              <Button variant="outline" size="sm" onClick={copyShareUrl}>
                <Copy className="w-4 h-4 mr-1" /> URL
              </Button>
            </div>
            
            {qrAnalytics && (
              <div className="mt-4 pt-4 border-t border-border w-full text-center">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="font-bold text-lg text-foreground">{qrAnalytics.total_scans}</p>
                    <p className="text-muted-foreground">Total</p>
                  </div>
                  <div>
                    <p className="font-bold text-lg text-foreground">{qrAnalytics.this_week}</p>
                    <p className="text-muted-foreground">Week</p>
                  </div>
                  <div>
                    <p className="font-bold text-lg text-foreground">{qrAnalytics.this_month}</p>
                    <p className="text-muted-foreground">Month</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Referral Funnel */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Referral Funnel
          </h3>
          
          {loading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-sm">Signups from Referrals</span>
                <span className="font-bold text-lg">{referralStats?.funnel?.total_signups || 0}</span>
              </div>
              <div className="flex items-center justify-center">
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <span className="text-sm">First Purchases</span>
                <span className="font-bold text-lg">{referralStats?.funnel?.first_purchases || 0}</span>
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Conversion: <span className="font-bold text-primary">{referralStats?.funnel?.conversion_rate || 0}%</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Top Referrers */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Top Referrers
          </h3>
          
          {loading ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : referralStats?.top_referrers?.length > 0 ? (
            <div className="space-y-1">
              {referralStats.top_referrers.map((ref, i) => (
                <ReferrerRow
                  key={ref.user_id}
                  rank={i + 1}
                  name={ref.name}
                  referrals={ref.successful_referrals}
                  isAmbassador={ref.successful_referrals >= 25}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No referrals yet</p>
          )}
        </div>
      </div>
      
      {/* Coupon Stats */}
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
          <Ticket className="w-5 h-5 text-primary" />
          Coupon Performance
        </h3>
        
        {loading ? (
          <div className="h-32 bg-muted animate-pulse rounded" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-foreground">{couponStats?.summary?.issued || 0}</p>
              <p className="text-sm text-muted-foreground">Issued</p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{couponStats?.summary?.redeemed || 0}</p>
              <p className="text-sm text-muted-foreground">Redeemed</p>
            </div>
            <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{couponStats?.summary?.active || 0}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{couponStats?.summary?.redemption_rate || 0}%</p>
              <p className="text-sm text-muted-foreground">Redemption Rate</p>
            </div>
          </div>
        )}
        
        {couponStats?.by_discount && couponStats.by_discount.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="text-sm font-medium mb-3">By Discount Type</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-2">Discount</th>
                    <th className="pb-2 text-right">Issued</th>
                    <th className="pb-2 text-right">Redeemed</th>
                    <th className="pb-2 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {couponStats.by_discount.map(d => (
                    <tr key={d.discount} className="border-t border-border">
                      <td className="py-2 font-medium">{d.discount}% off</td>
                      <td className="py-2 text-right">{d.issued}</td>
                      <td className="py-2 text-right">{d.redeemed}</td>
                      <td className="py-2 text-right">{d.issued > 0 ? Math.round((d.redeemed / d.issued) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {/* Quick Links */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="font-heading text-sm mb-3">Quick Links</h3>
        <div className="flex flex-wrap gap-2">
          <a 
            href="/share" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
          >
            <Camera className="w-4 h-4" /> Share Page <ExternalLink className="w-3 h-3" />
          </a>
          <a 
            href="/bula-coffee-club" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
          >
            <Gift className="w-4 h-4" /> Coffee Club <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
